/**
 * «Войти как игрок» во вкладке браузера владельца.
 *
 * Вкладка игрока живёт в том же браузере, что и админка, а токены, черновики
 * и вход OIDC лежат в общем localStorage. Чтобы вкладки не перетирали друг
 * друга, во вкладке игрока localStorage подменяется её собственным
 * хранилищем (sessionStorage с префиксом): токен игрока виден там, где
 * страницы привыкли его читать, а хранилище админа не читается и не меняется.
 *
 * Обновить токен нельзя: refresh-токена нет, а тихий вход через провайдер
 * вернул бы токен самого админа. По истечении срока вкладка закрывается.
 *
 * Передача: админка открывает /impersonate#<данные>; модуль (импортируется
 * первым в index.tsx) забирает их до старта приложения и заменяет адрес,
 * чтобы токен не остался в истории.
 */
import { USER_TOKEN_KEY } from './authTokens';

export type TImpersonation = {
  token: string;
  /** unix-время окончания, секунды */
  expires_at: number;
  login: string;
  user_id: number;
  mode: 'view' | 'full';
};

const SESSION_KEY = 'amazing_impersonation';
const LS_PREFIX = 'imp.ls:';
/** Ключи, которые вкладка игрока читает из хранилища браузера (только чтение) */
const READ_THROUGH = new Set(['cookie_consent']);
export const IMPERSONATE_PATH = '/impersonate';

const ISSUER = process.env.REACT_APP_OIDC_ISSUER ?? 'http://localhost:3002/oidc';
const CLIENT_ID = process.env.REACT_APP_OIDC_CLIENT_ID ?? 'amazing_frontend';
const OIDC_USER_KEY = `oidc.user:${ISSUER}:${CLIENT_ID}`;

let current: TImpersonation | null = null;

export function getImpersonation(): TImpersonation | null {
  return current;
}

export function isImpersonating(): boolean {
  return current !== null;
}

/** Ссылка, которую админка открывает в новой вкладке */
export function impersonationUrl(data: TImpersonation): string {
  return `${IMPERSONATE_PATH}#${encodeURIComponent(JSON.stringify(data))}`;
}

function parse(raw: string | null): TImpersonation | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<TImpersonation>;
    if (typeof v.token !== 'string' || typeof v.expires_at !== 'number' || typeof v.login !== 'string') return null;
    return { token: v.token, expires_at: v.expires_at, login: v.login, user_id: Number(v.user_id), mode: v.mode === 'full' ? 'full' : 'view' };
  } catch {
    return null;
  }
}

/** Пользователь OIDC в формате oidc-client-ts: без refresh-токена и id_token */
function oidcUserJson(imp: TImpersonation): string {
  return JSON.stringify({
    access_token: imp.token,
    token_type: 'Bearer',
    scope: 'openid profile email',
    expires_at: imp.expires_at,
    profile: { sub: String(imp.user_id), login: imp.login, iss: ISSUER, aud: CLIENT_ID, exp: imp.expires_at, iat: 0 },
  });
}

/** Подмена localStorage только в этой вкладке: собственное хранилище плюс токен игрока */
function patchLocalStorage(imp: TImpersonation, session: Storage): void {
  const proto = Object.getPrototypeOf(window.localStorage) as Storage;
  const orig = {
    getItem: proto.getItem, setItem: proto.setItem, removeItem: proto.removeItem,
    key: proto.key, clear: proto.clear,
  };
  const local = window.localStorage;
  const fixed = new Map<string, string>([[USER_TOKEN_KEY, imp.token], [OIDC_USER_KEY, oidcUserJson(imp)]]);
  const ownKeys = (): string[] => {
    const out: string[] = [];
    for (let i = 0; i < session.length; i++) {
      const k = orig.key.call(session, i);
      if (k?.startsWith(LS_PREFIX)) out.push(k.slice(LS_PREFIX.length));
    }
    return out;
  };

  proto.getItem = function (this: Storage, key: string) {
    if (this !== local) return orig.getItem.call(this, key);
    if (fixed.has(key)) return fixed.get(key) ?? null;
    const own = orig.getItem.call(session, LS_PREFIX + key);
    // Согласие на cookie дал сам владелец в этом браузере: не спрашиваем снова
    return own ?? (READ_THROUGH.has(key) ? orig.getItem.call(this, key) : null);
  };
  proto.setItem = function (this: Storage, key: string, value: string) {
    if (this !== local) return orig.setItem.call(this, key, value);
    if (fixed.has(key)) return undefined;
    return orig.setItem.call(session, LS_PREFIX + key, value);
  };
  proto.removeItem = function (this: Storage, key: string) {
    if (this !== local) return orig.removeItem.call(this, key);
    if (fixed.has(key)) return undefined;
    return orig.removeItem.call(session, LS_PREFIX + key);
  };
  proto.key = function (this: Storage, index: number) {
    if (this !== local) return orig.key.call(this, index);
    return ownKeys()[index] ?? null;
  };
  proto.clear = function (this: Storage) {
    if (this !== local) return orig.clear.call(this);
    ownKeys().forEach(k => orig.removeItem.call(session, LS_PREFIX + k));
    return undefined;
  };
  const lengthDesc = Object.getOwnPropertyDescriptor(proto, 'length');
  if (lengthDesc?.get) {
    const getLength = lengthDesc.get;
    Object.defineProperty(proto, 'length', {
      configurable: true,
      get(this: Storage) { return this === local ? ownKeys().length : getLength.call(this); },
    });
  }
}

/**
 * Вызывается до старта приложения. Забирает данные из адреса /impersonate#…
 * или из sessionStorage вкладки (после перезагрузки) и включает подмену.
 */
export function installImpersonation(): void {
  if (typeof window === 'undefined') return;
  let session: Storage;
  try { session = window.sessionStorage; } catch { return; }

  if (window.location.pathname === IMPERSONATE_PATH) {
    const data = parse(decodeURIComponent(window.location.hash.slice(1)));
    // Новая вкладка получает копию sessionStorage админки: начинаем с чистой
    try { session.clear(); } catch { /* ignore */ }
    if (data) session.setItem(SESSION_KEY, JSON.stringify(data));
    window.history.replaceState(null, '', data ? '/panel' : '/');
  }

  const imp = parse(session.getItem(SESSION_KEY));
  if (!imp) return;
  if (imp.expires_at * 1000 <= Date.now()) {
    endImpersonation(session);
    return;
  }
  current = imp;
  patchLocalStorage(imp, session);
}

/** Выход: стираем всё, что вкладка накопила, и закрываем её */
export function endImpersonation(session: Storage = window.sessionStorage): void {
  try { session.clear(); } catch { /* ignore */ }
  current = null;
  window.close();
  // Вкладку открыл не скрипт — закрыть нельзя; уходим на главную,
  // перезагрузка уже без подмены
  window.location.replace('/');
}

/** Ошибка вместо обновления токена или повторного входа во вкладке игрока */
export class ImpersonationEndedError extends Error {
  constructor() { super('Вход под игроком завершён'); }
}
